import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import TWEEN from '@tweenjs/tween.js';
import { realCities as cities, getCitiesByRegion, searchCities, getCityById, regionNames, dataStats } from './data/real_cities.js';
import { getFoodDetail } from './data/food_details.js';

class FoodGlobe {
  constructor() {
    this.container = document.getElementById('globe-canvas');
    this.width = window.innerWidth;
    this.height = window.innerHeight;
    this.autoRotate = true;
    this.showLabels = false;
    this.currentMode = 'markers';
    this.currentRegion = 'all';
    this.selectedCity = null;
    this.cityMarkers = [];
    this.heatmapPoints = [];
    this.visibleCities = [...cities];
    this.hoveredCity = null;
    this.tooltip = null;
    this.foodModal = null;
    this.labelsContainer = null;
    this.cityLabels = [];
    
    this.init();
  }

  init() {
    this.createScene();
    this.createCamera();
    this.createRenderer();
    this.createLights();
    this.createGlobe();
    this.createCityMarkers();
    this.createControls();
    this.createRaycaster();
    this.setupUI();
    this.createLabels();
    this.setupEventListeners();
    this.animate();
    
    document.getElementById('loading').classList.add('hidden');
  }

  createScene() {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0a0a1a);
    
    const starGeometry = new THREE.BufferGeometry();
    const starCount = 5000;
    const positions = new Float32Array(starCount * 3);
    
    for (let i = 0; i < starCount * 3; i += 3) {
      positions[i] = (Math.random() - 0.5) * 2000;
      positions[i + 1] = (Math.random() - 0.5) * 2000;
      positions[i + 2] = (Math.random() - 0.5) * 2000;
    }
    
    starGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    
    const starMaterial = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 1,
      transparent: true,
      opacity: 0.8
    });
    
    this.stars = new THREE.Points(starGeometry, starMaterial);
    this.scene.add(this.stars);
  }

  createCamera() {
    this.camera = new THREE.PerspectiveCamera(
      45,
      this.width / this.height,
      0.1,
      1000
    );
    this.camera.position.z = 3.5;
  }

  createRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.container,
      antialias: true,
      alpha: true
    });
    this.renderer.setSize(this.width, this.height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
  }

  createLights() {
    const ambientLight = new THREE.AmbientLight(0x404060, 0.5);
    this.scene.add(ambientLight);
    
    const sunLight = new THREE.DirectionalLight(0xffffff, 1.5);
    sunLight.position.set(5, 3, 5);
    sunLight.castShadow = true;
    this.scene.add(sunLight);
    
    const fillLight = new THREE.DirectionalLight(0x6080ff, 0.5);
    fillLight.position.set(-5, -3, -5);
    this.scene.add(fillLight);
  }

  createGlobe() {
    const earthGeometry = new THREE.SphereGeometry(1, 64, 64);
    
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#1a3a5c');
    gradient.addColorStop(0.3, '#1e4a6e');
    gradient.addColorStop(0.5, '#2d5a7e');
    gradient.addColorStop(0.7, '#1e4a6e');
    gradient.addColorStop(1, '#1a3a5c');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    ctx.fillStyle = '#2a7a4e';
    this.drawContinents(ctx, canvas.width, canvas.height);
    
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 0.5;
    for (let i = 0; i < 24; i++) {
      const x = (i / 24) * canvas.width;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
    }
    
    for (let i = 0; i < 12; i++) {
      const y = (i / 12) * canvas.height;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }
    
    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    
    const earthMaterial = new THREE.MeshPhongMaterial({
      map: texture,
      shininess: 15,
      specular: 0x333355,
      transparent: true,
      opacity: 0.95
    });
    
    this.earth = new THREE.Mesh(earthGeometry, earthMaterial);
    this.scene.add(this.earth);
    
    const atmosphereGeometry = new THREE.SphereGeometry(1.05, 64, 64);
    const atmosphereMaterial = new THREE.ShaderMaterial({
      vertexShader: `
        varying vec3 vNormal;
        void main() {
          vNormal = normalize(normalMatrix * normal);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        varying vec3 vNormal;
        void main() {
          float intensity = pow(0.7 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 2.0);
          gl_FragColor = vec4(0.3, 0.6, 1.0, 1.0) * intensity;
        }
      `,
      blending: THREE.AdditiveBlending,
      side: THREE.BackSide,
      transparent: true
    });
    
    this.atmosphere = new THREE.Mesh(atmosphereGeometry, atmosphereMaterial);
    this.scene.add(this.atmosphere);
  }

  drawContinents(ctx, width, height) {
    const continents = [
      { points: this.generateContinentPoints(0.15, 0.2, 0.25, 0.35) },
      { points: this.generateContinentPoints(0.45, 0.15, 0.15, 0.25) },
      { points: this.generateContinentPoints(0.5, 0.3, 0.12, 0.2) },
      { points: this.generateContinentPoints(0.25, 0.4, 0.15, 0.3) },
      { points: this.generateContinentPoints(0.65, 0.45, 0.15, 0.25) },
      { points: this.generateContinentPoints(0.78, 0.7, 0.1, 0.12) },
      { points: this.generateContinentPoints(0.1, 0.7, 0.18, 0.2) }
    ];
    
    ctx.fillStyle = '#2a7a4e';
    continents.forEach(continent => {
      ctx.beginPath();
      continent.points.forEach((point, index) => {
        const x = point.x * width;
        const y = point.y * height;
        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.closePath();
      ctx.fill();
      
      ctx.fillStyle = '#3a9a6e';
      ctx.globalAlpha = 0.5;
      ctx.beginPath();
      continent.points.forEach((point, index) => {
        const x = (point.x + 0.01) * width;
        const y = (point.y + 0.01) * height;
        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 1.0;
      ctx.fillStyle = '#2a7a4e';
    });
  }

  generateContinentPoints(centerX, centerY, width, height) {
    const points = [];
    const numPoints = 12;
    
    for (let i = 0; i < numPoints; i++) {
      const angle = (i / numPoints) * Math.PI * 2;
      const radiusX = width * (0.7 + Math.random() * 0.6);
      const radiusY = height * (0.7 + Math.random() * 0.6);
      
      points.push({
        x: centerX + Math.cos(angle) * radiusX,
        y: centerY + Math.sin(angle) * radiusY
      });
    }
    
    return points;
  }

  createCityMarkers() {
    this.cityMarkers.forEach(marker => {
      this.scene.remove(marker.mesh);
    });
    this.cityMarkers = [];
    
    this.visibleCities.forEach(city => {
      const marker = this.createMarker(city);
      this.cityMarkers.push(marker);
      this.scene.add(marker.mesh);
    });
  }

  createMarker(city) {
    const radius = 1.01;
    const latitude = city.latitude * (Math.PI / 180);
    const longitude = city.longitude * (Math.PI / 180);
    
    const x = radius * Math.cos(latitude) * Math.cos(longitude);
    const y = radius * Math.sin(latitude);
    const z = radius * Math.cos(latitude) * Math.sin(longitude);
    
    const heatScore = city.heatScore;
    let color;
    if (heatScore >= 90) {
      color = new THREE.Color(0xff6b6b);
    } else if (heatScore >= 80) {
      color = new THREE.Color(0xffd93d);
    } else {
      color = new THREE.Color(0x6bcb77);
    }
    
    const size = 0.015 + (heatScore / 100) * 0.02;
    const geometry = new THREE.SphereGeometry(size, 16, 16);
    const material = new THREE.MeshBasicMaterial({
      color: color,
      transparent: true,
      opacity: 0.9
    });
    
    const markerMesh = new THREE.Mesh(geometry, material);
    
    const ringGeometry = new THREE.RingGeometry(size * 1.5, size * 2, 32);
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: color,
      transparent: true,
      opacity: 0.4,
      side: THREE.DoubleSide
    });
    
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    
    const pulseGeometry = new THREE.RingGeometry(size * 0.8, size * 1.2, 32);
    const pulseMaterial = new THREE.MeshBasicMaterial({
      color: color,
      transparent: true,
      opacity: 0.6,
      side: THREE.DoubleSide
    });
    
    const pulse = new THREE.Mesh(pulseGeometry, pulseMaterial);
    
    const group = new THREE.Group();
    group.position.set(x, y, z);
    group.lookAt(0, 0, 0);
    group.add(markerMesh);
    group.add(ring);
    group.add(pulse);
    
    group.userData = {
      city: city,
      basePosition: new THREE.Vector3(x, y, z),
      pulseMesh: pulse,
      markerMesh: markerMesh,
      ring: ring,
      baseSize: size,
      color: color,
      pulseTime: Math.random() * Math.PI * 2
    };
    
    return {
      mesh: group,
      city: city,
      position: new THREE.Vector3(x, y, z)
    };
  }

  createHeatmap() {
    this.heatmapPoints.forEach(point => {
      this.scene.remove(point);
    });
    this.heatmapPoints = [];
    
    if (this.currentMode !== 'heatmap') return;
    
    this.visibleCities.forEach(city => {
      const heatPoint = this.createHeatPoint(city);
      this.heatmapPoints.push(heatPoint);
      this.scene.add(heatPoint);
    });
  }

  createHeatPoint(city) {
    const radius = 1.001;
    const latitude = city.latitude * (Math.PI / 180);
    const longitude = city.longitude * (Math.PI / 180);
    
    const x = radius * Math.cos(latitude) * Math.cos(longitude);
    const y = radius * Math.sin(latitude);
    const z = radius * Math.cos(latitude) * Math.sin(longitude);
    
    const heatScore = city.heatScore;
    const size = 0.15 + (heatScore / 100) * 0.2;
    
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    const ctx = canvas.getContext('2d');
    
    const gradient = ctx.createRadialGradient(
      64, 64, 0,
      64, 64, 64
    );
    
    if (heatScore >= 90) {
      gradient.addColorStop(0, 'rgba(255, 107, 107, 0.8)');
      gradient.addColorStop(0.3, 'rgba(255, 107, 107, 0.4)');
      gradient.addColorStop(1, 'rgba(255, 107, 107, 0)');
    } else if (heatScore >= 80) {
      gradient.addColorStop(0, 'rgba(255, 217, 61, 0.8)');
      gradient.addColorStop(0.3, 'rgba(255, 217, 61, 0.4)');
      gradient.addColorStop(1, 'rgba(255, 217, 61, 0)');
    } else {
      gradient.addColorStop(0, 'rgba(107, 203, 119, 0.8)');
      gradient.addColorStop(0.3, 'rgba(107, 203, 119, 0.4)');
      gradient.addColorStop(1, 'rgba(107, 203, 119, 0)');
    }
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 128, 128);
    
    const texture = new THREE.CanvasTexture(canvas);
    const geometry = new THREE.PlaneGeometry(size, size);
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      opacity: 0.7,
      side: THREE.DoubleSide,
      depthWrite: false
    });
    
    const heatPoint = new THREE.Mesh(geometry, material);
    heatPoint.position.set(x, y, z);
    heatPoint.lookAt(0, 0, 0);
    
    heatPoint.userData = {
      city: city,
      basePosition: new THREE.Vector3(x, y, z)
    };
    
    return heatPoint;
  }

  createControls() {
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.enableZoom = true;
    this.controls.enablePan = false;
    this.controls.minDistance = 1.5;
    this.controls.maxDistance = 8;
    this.controls.autoRotate = this.autoRotate;
    this.controls.autoRotateSpeed = 0.5;
    this.controls.target.set(0, 0, 0);
    this.controls.update();
  }

  createRaycaster() {
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
  }

  setupUI() {
    this.searchInput = document.getElementById('search-input');
    this.regionFilter = document.getElementById('region-filter');
    this.modeMarkers = document.getElementById('mode-markers');
    this.modeHeatmap = document.getElementById('mode-heatmap');
    this.autoRotateToggle = document.getElementById('auto-rotate');
    this.showLabelsToggle = document.getElementById('show-labels');
    this.infoPanel = document.getElementById('info-panel');
    this.closePanelBtn = document.getElementById('close-panel');
    
    this.createTooltip();
  }

  createTooltip() {
    this.tooltip = document.createElement('div');
    this.tooltip.className = 'tooltip';
    this.tooltip.style.display = 'none';
    document.body.appendChild(this.tooltip);
  }

  createLabels() {
    this.labelsContainer = document.getElementById('labels-container');
    if (!this.labelsContainer) return;
    
    this.labelsContainer.innerHTML = '';
    this.cityLabels = [];
    
    this.cityMarkers.forEach(marker => {
      const city = marker.city;
      const label = document.createElement('div');
      label.className = 'city-label';
      label.textContent = city.name;
      
      if (city.heatScore >= 90) {
        label.classList.add('hot-high');
      } else if (city.heatScore >= 80) {
        label.classList.add('hot-medium');
      } else {
        label.classList.add('hot-low');
      }
      
      this.labelsContainer.appendChild(label);
      this.cityLabels.push({
        city: city,
        element: label,
        position: marker.position.clone()
      });
    });
    
    this.updateLabelsVisibility();
  }

  updateLabelsVisibility() {
    this.cityLabels.forEach(label => {
      if (this.showLabels) {
        label.element.classList.add('visible');
      } else {
        label.element.classList.remove('visible');
        label.element.style.opacity = '';
      }
    });
  }

  updateLabelsPosition() {
    if (!this.showLabels || this.cityLabels.length === 0) return;
    
    const cameraFromCenter = this.camera.position.clone().normalize();
    
    this.cityLabels.forEach(label => {
      const position = label.position.clone();
      
      const pointFromCenter = position.clone().normalize();
      const dotProduct = pointFromCenter.dot(cameraFromCenter);
      
      if (dotProduct < 0) {
        label.element.style.opacity = 0;
        return;
      }
      
      const vector = position.clone();
      vector.project(this.camera);
      
      if (vector.z > 1 || vector.z < -1 || 
          vector.x < -1 || vector.x > 1 || 
          vector.y < -1 || vector.y > 1) {
        label.element.style.opacity = 0;
        return;
      }
      
      const x = (vector.x * 0.5 + 0.5) * this.width;
      const y = (-vector.y * 0.5 + 0.5) * this.height;
      
      const opacity = Math.min(1, Math.max(0.3, dotProduct * 1.5));
      const labelWidth = label.element.offsetWidth || 60;
      
      label.element.style.left = (x - labelWidth / 2) + 'px';
      label.element.style.top = (y - 18) + 'px';
      label.element.style.opacity = opacity;
      label.element.style.pointerEvents = 'none';
    });
  }

  updateTooltip(city, event) {
    if (!city) {
      this.tooltip.style.display = 'none';
      return;
    }
    
    this.tooltip.innerHTML = `
      <div class="tooltip-city">${city.name}</div>
      <div class="tooltip-country">${city.country}</div>
      <div class="tooltip-foods">
        ${city.foods.slice(0, 3).map(food => `<span class="tooltip-food">${food}</span>`).join('')}
      </div>
    `;
    
    this.tooltip.style.display = 'block';
    this.tooltip.style.left = (event.clientX + 15) + 'px';
    this.tooltip.style.top = (event.clientY + 15) + 'px';
  }

  setupEventListeners() {
    window.addEventListener('resize', () => this.onWindowResize());
    
    this.searchInput.addEventListener('input', (e) => this.onSearch(e));
    this.regionFilter.addEventListener('change', (e) => this.onRegionChange(e));
    
    this.modeMarkers.addEventListener('click', () => this.setMode('markers'));
    this.modeHeatmap.addEventListener('click', () => this.setMode('heatmap'));
    
    this.autoRotateToggle.addEventListener('change', (e) => {
      this.autoRotate = e.target.checked;
      this.controls.autoRotate = this.autoRotate;
    });
    
    this.showLabelsToggle.addEventListener('change', (e) => {
      this.showLabels = e.target.checked;
      this.updateLabelsVisibility();
    });
    
    this.closePanelBtn.addEventListener('click', () => this.closeInfoPanel());
    
    const modalCloseBtn = document.getElementById('modal-close');
    if (modalCloseBtn) {
      modalCloseBtn.addEventListener('click', () => this.closeFoodDetail());
    }
    
    const modalOverlay = document.getElementById('food-modal');
    if (modalOverlay) {
      modalOverlay.addEventListener('click', (e) => {
        if (e.target === modalOverlay) {
          this.closeFoodDetail();
        }
      });
    }
    
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (this.foodModal) {
          this.closeFoodDetail();
        } else if (this.selectedCity) {
          this.closeInfoPanel();
        }
      }
    });
    
    this.renderer.domElement.addEventListener('mousemove', (e) => this.onMouseMove(e));
    this.renderer.domElement.addEventListener('click', (e) => this.onMouseClick(e));
  }

  onWindowResize() {
    this.width = window.innerWidth;
    this.height = window.innerHeight;
    
    this.camera.aspect = this.width / this.height;
    this.camera.updateProjectionMatrix();
    
    this.renderer.setSize(this.width, this.height);
  }

  onSearch(e) {
    const query = e.target.value.trim();
    
    if (query === '') {
      this.visibleCities = getCitiesByRegion(this.currentRegion);
    } else {
      const searchResults = searchCities(query);
      if (this.currentRegion !== 'all') {
        this.visibleCities = searchResults.filter(city => city.region === this.currentRegion);
      } else {
        this.visibleCities = searchResults;
      }
    }
    
    this.updateMarkers();
  }

  onRegionChange(e) {
    this.currentRegion = e.target.value;
    
    const query = this.searchInput.value.trim();
    if (query === '') {
      this.visibleCities = getCitiesByRegion(this.currentRegion);
    } else {
      const searchResults = searchCities(query);
      if (this.currentRegion !== 'all') {
        this.visibleCities = searchResults.filter(city => city.region === this.currentRegion);
      } else {
        this.visibleCities = searchResults;
      }
    }
    
    this.updateMarkers();
  }

  setMode(mode) {
    this.currentMode = mode;
    
    this.modeMarkers.classList.toggle('active', mode === 'markers');
    this.modeHeatmap.classList.toggle('active', mode === 'heatmap');
    
    if (mode === 'markers') {
      this.heatmapPoints.forEach(point => {
        this.scene.remove(point);
      });
      this.heatmapPoints = [];
      this.createCityMarkers();
      this.createLabels();
    } else {
      this.cityMarkers.forEach(marker => {
        this.scene.remove(marker.mesh);
      });
      this.cityMarkers = [];
      this.cityLabels = [];
      if (this.labelsContainer) {
        this.labelsContainer.innerHTML = '';
      }
      this.createHeatmap();
    }
  }

  updateMarkers() {
    if (this.currentMode === 'markers') {
      this.createCityMarkers();
      this.createLabels();
    } else {
      this.cityLabels = [];
      if (this.labelsContainer) {
        this.labelsContainer.innerHTML = '';
      }
      this.createHeatmap();
    }
  }

  onMouseMove(event) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    
    let intersects = [];
    
    if (this.currentMode === 'markers') {
      const meshes = this.cityMarkers.map(marker => marker.mesh.userData.markerMesh);
      this.raycaster.setFromCamera(this.mouse, this.camera);
      intersects = this.raycaster.intersectObjects(meshes, false);
    } else {
      this.raycaster.setFromCamera(this.mouse, this.camera);
      intersects = this.raycaster.intersectObjects(this.heatmapPoints, false);
    }
    
    if (intersects.length > 0) {
      let city;
      if (this.currentMode === 'markers') {
        const markerIndex = this.cityMarkers.findIndex(marker => 
          marker.mesh.userData.markerMesh === intersects[0].object
        );
        if (markerIndex !== -1) {
          city = this.cityMarkers[markerIndex].city;
        }
      } else {
        city = intersects[0].object.userData.city;
      }
      
      if (city && city !== this.hoveredCity) {
        this.hoveredCity = city;
        document.body.style.cursor = 'pointer';
        this.updateTooltip(city, event);
        
        if (this.currentMode === 'markers') {
          this.cityMarkers.forEach(marker => {
            const scale = marker.city === city ? 1.5 : 1;
            marker.mesh.scale.set(scale, scale, scale);
          });
        }
      } else if (city) {
        this.updateTooltip(city, event);
      }
    } else {
      this.hoveredCity = null;
      document.body.style.cursor = 'default';
      this.tooltip.style.display = 'none';
      
      if (this.currentMode === 'markers') {
        this.cityMarkers.forEach(marker => {
          marker.mesh.scale.set(1, 1, 1);
        });
      }
    }
  }

  onMouseClick(event) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    
    let intersects = [];
    
    if (this.currentMode === 'markers') {
      const meshes = this.cityMarkers.map(marker => marker.mesh.userData.markerMesh);
      this.raycaster.setFromCamera(this.mouse, this.camera);
      intersects = this.raycaster.intersectObjects(meshes, false);
    } else {
      this.raycaster.setFromCamera(this.mouse, this.camera);
      intersects = this.raycaster.intersectObjects(this.heatmapPoints, false);
    }
    
    if (intersects.length > 0) {
      let city;
      if (this.currentMode === 'markers') {
        const markerIndex = this.cityMarkers.findIndex(marker => 
          marker.mesh.userData.markerMesh === intersects[0].object
        );
        if (markerIndex !== -1) {
          city = this.cityMarkers[markerIndex].city;
        }
      } else {
        city = intersects[0].object.userData.city;
      }
      
      if (city) {
        this.selectedCity = city;
        this.showInfoPanel(city);
        this.focusOnCity(city);
      }
    }
  }

  focusOnCity(city) {
    const latitude = city.latitude * (Math.PI / 180);
    const longitude = city.longitude * (Math.PI / 180);
    
    const radius = 2.5;
    const targetX = radius * Math.cos(latitude) * Math.cos(longitude);
    const targetY = radius * Math.sin(latitude);
    const targetZ = radius * Math.cos(latitude) * Math.sin(longitude);
    
    const startPosition = this.camera.position.clone();
    const startTarget = this.controls.target.clone();
    
    const tween = new TWEEN.Tween({
      camX: startPosition.x,
      camY: startPosition.y,
      camZ: startPosition.z
    })
    .to({
      camX: targetX,
      camY: targetY,
      camZ: targetZ
    }, 1500)
    .easing(TWEEN.Easing.Quadratic.InOut)
    .onUpdate((obj) => {
      this.camera.position.set(obj.camX, obj.camY, obj.camZ);
      this.controls.update();
    })
    .start();
    
    this.autoRotate = false;
    this.controls.autoRotate = false;
    this.autoRotateToggle.checked = false;
  }

  showInfoPanel(city) {
    document.getElementById('city-name').textContent = city.name;
    
    const nameEnElement = document.getElementById('city-name-en');
    if (city.nameEn) {
      nameEnElement.textContent = city.nameEn;
      nameEnElement.style.display = 'block';
    } else {
      nameEnElement.style.display = 'none';
    }
    
    document.getElementById('country-name').textContent = city.country;
    document.getElementById('region-name').textContent = regionNames[city.region] || city.region;
    document.getElementById('coordinates').textContent = `${city.latitude.toFixed(4)}°, ${city.longitude.toFixed(4)}°`;
    
    const descriptionElement = document.getElementById('city-description');
    const descriptionSection = document.getElementById('description-section');
    if (city.description) {
      descriptionElement.textContent = city.description;
      descriptionSection.style.display = 'block';
    } else {
      descriptionSection.style.display = 'none';
    }
    
    const foodList = document.getElementById('food-list');
    foodList.innerHTML = '';
    
    city.foods.forEach(food => {
      const foodDetail = getFoodDetail(food);
      const span = document.createElement('span');
      span.className = 'food-tag';
      span.textContent = food;
      
      if (foodDetail) {
        span.classList.add('has-detail');
        span.addEventListener('click', () => {
          this.showFoodDetail(foodDetail);
        });
        span.title = '点击查看详情';
      }
      
      foodList.appendChild(span);
    });
    
    const heatScore = city.heatScore;
    document.getElementById('heat-score').textContent = heatScore;
    document.getElementById('heat-fill').style.width = heatScore + '%';
    
    const dataSourceElement = document.getElementById('data-source');
    const dataSourceSection = document.getElementById('data-source-section');
    if (city.source) {
      dataSourceElement.textContent = `数据来源: ${city.source}`;
      dataSourceSection.style.display = 'block';
    } else {
      dataSourceSection.style.display = 'none';
    }
    
    this.infoPanel.classList.remove('hidden');
  }

  showFoodDetail(foodDetail) {
    if (!foodDetail) return;
    
    document.getElementById('modal-food-name').textContent = foodDetail.name;
    document.getElementById('modal-food-name-en').textContent = foodDetail.nameEn;
    
    const tagsContainer = document.getElementById('modal-food-tags');
    tagsContainer.innerHTML = foodDetail.tags.map(tag => 
      `<span class="tag">${tag}</span>`
    ).join('');
    
    document.getElementById('modal-description').textContent = foodDetail.description;
    document.getElementById('modal-history').textContent = foodDetail.history;
    
    const ingredientsList = document.getElementById('modal-ingredients');
    ingredientsList.innerHTML = foodDetail.ingredients.map(ingredient => 
      `<span class="ingredient-item">${ingredient}</span>`
    ).join('');
    
    document.getElementById('modal-taste').textContent = foodDetail.taste;
    document.getElementById('modal-price').textContent = foodDetail.priceRange;
    
    const restaurantsList = document.getElementById('modal-restaurants');
    restaurantsList.innerHTML = foodDetail.recommendedRestaurants.map(restaurant => 
      `<div class="restaurant-item">
        <span class="restaurant-icon"></span>
        <span class="restaurant-name">${restaurant}</span>
      </div>`
    ).join('');
    
    const funFactsList = document.getElementById('modal-fun-facts');
    funFactsList.innerHTML = foodDetail.funFacts.map((fact, index) => 
      `<div class="fact-item">
        <span class="fact-number">${index + 1}</span>
        <span class="fact-text">${fact}</span>
      </div>`
    ).join('');
    
    document.getElementById('modal-category').textContent = foodDetail.category;
    document.getElementById('modal-popularity').textContent = foodDetail.popularity + ' / 100';
    
    const modalOverlay = document.getElementById('food-modal');
    modalOverlay.classList.remove('hidden');
    
    this.foodModal = foodDetail;
  }

  closeFoodDetail() {
    const modalOverlay = document.getElementById('food-modal');
    modalOverlay.classList.add('hidden');
    this.foodModal = null;
  }

  closeInfoPanel() {
    this.infoPanel.classList.add('hidden');
    this.selectedCity = null;
  }

  animate() {
    requestAnimationFrame(() => this.animate());
    
    TWEEN.update();
    this.controls.update();
    
    if (!this.controls.target.equals(new THREE.Vector3(0, 0, 0))) {
      this.controls.target.lerp(new THREE.Vector3(0, 0, 0), 0.1);
    }
    
    this.updateLabelsPosition();
    
    const time = Date.now() * 0.001;
    
    this.cityMarkers.forEach(marker => {
      const userData = marker.mesh.userData;
      if (userData.pulseMesh) {
        const pulseScale = 1 + Math.sin(time * 2 + userData.pulseTime) * 0.2;
        userData.pulseMesh.scale.set(pulseScale, pulseScale, pulseScale);
        
        const pulseOpacity = 0.3 + Math.sin(time * 2 + userData.pulseTime) * 0.2;
        userData.pulseMesh.material.opacity = pulseOpacity;
      }
      
      if (userData.markerMesh && this.selectedCity === marker.city) {
        const floatOffset = Math.sin(time * 3) * 0.01;
        userData.markerMesh.position.set(0, 0, floatOffset);
        userData.ring.position.set(0, 0, floatOffset);
        userData.pulseMesh.position.set(0, 0, floatOffset);
      } else {
        userData.markerMesh.position.set(0, 0, 0);
        userData.ring.position.set(0, 0, 0);
        userData.pulseMesh.position.set(0, 0, 0);
      }
    });
    
    if (this.autoRotate) {
      this.stars.rotation.y += 0.0001;
    }
    
    this.renderer.render(this.scene, this.camera);
  }
}

window.addEventListener('DOMContentLoaded', () => {
  new FoodGlobe();
});
